import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Zap, ArrowLeft, Mail, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [orgShortname, setOrgShortname] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, {
        email: email.toLowerCase().trim(),
        orgSlug: orgShortname.toLowerCase().trim()
      });

      if (response.data.success) {
        setSent(true);
        toast.success('Email sent! Check your inbox.');
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
        <div className="absolute top-4 left-4 z-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/login')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <CardHeader className="text-center pt-16 pb-6">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: '#2563EB'}}>
              <Mail className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            Forgot Password/Username?
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {sent ? "Check Your Email" : "Enter your email to receive reset instructions"}
          </p>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="h-12 text-base border border-gray-200 focus:border-blue-500"
                  autoFocus
                />
                <p className="text-xs text-gray-500">
                  💡 We'll search both organization owners and team members
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="orgShortname" className="text-sm font-semibold text-gray-700">
                  Organization Shortname <span className="font-normal text-gray-400">(recommended)</span>
                </Label>
                <Input
                  id="orgShortname"
                  data-testid="forgot-org-shortname-input"
                  type="text"
                  placeholder="e.g. funbeachwatersports"
                  value={orgShortname}
                  onChange={(e) => setOrgShortname(e.target.value)}
                  disabled={loading}
                  className="h-12 text-base border border-gray-200 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500">
                  🎯 If you belong to more than one organization, enter its shortname so we reset the right account.
                </p>
              </div>

              <Button 
                type="submit" 
                data-testid="forgot-submit-btn"
                className="w-full h-12 text-base font-semibold"
                style={{background: '#2563EB'}}
                disabled={loading || !email.trim()}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </span>
                ) : (
                  "Send Reset Instructions"
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              
              <h3 className="text-xl font-bold mb-2">Email Sent!</h3>
              <p className="text-sm text-gray-600 mb-4">
                We sent password reset instructions to <span className="font-semibold">{email}</span>
              </p>
              
              <p className="text-xs text-gray-500 mb-6">
                The email includes your username and a link to reset your password.
              </p>

              <p className="text-xs text-gray-500 mb-6">
                Check your inbox (and spam folder)
              </p>

              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 text-base font-semibold"
                style={{background: '#2563EB'}}
              >
                Back to Login
              </Button>
            </div>
          )}
        </CardContent>
        
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPassword;
